import style from "./ChoosePlan.module.css";

import Icon from "@/ui/icon/Icon";
import Button from "@/ui/button/Button";

import { Navigate, useNavigate } from "react-router-dom";
import { successToast } from "@/ui/toast/Toast";
import { useSubscribe } from "@/features/payments/hooks/useSubscribe";
import Spinner from "@/features/forms/components/Spinner";
import { useUser } from "../../hooks/useUser";
import { useEffect, useState } from "react";
import { usePriceInfo } from "@/features/payments/hooks/usePriceInfo";

// Import Monri payment security logos
import monriBadge from "@/assets/images/payment/security_programs/monri_badge.png";
import mastercardIdCheck from "@/assets/images/payment/security_programs/mc_idcheck_hrz_rgb_pos.png";
import visaSecure from "@/assets/images/payment/security_programs/visa-secure-blk_120dpi.jpg";

function ChoosePlan() {
	const navigate = useNavigate();
	const { subscribe } = useSubscribe();
	const { user } = useUser();

	const { priceInfo, isPending } = usePriceInfo();

	useEffect(() => {
		const shouldSkipPayment =
			localStorage.getItem("skipPayment") === "true";
		const selectedPlan = localStorage.getItem("selectedPlan");

		// Exit early if conditions aren't met
		if (!shouldSkipPayment || !user || user?.is_subscribed) return;

		const handleSubscription = async () => {
			localStorage.removeItem("skipPayment");
			localStorage.removeItem("selectedPlan");

			if (selectedPlan === "monthly" || selectedPlan == "yearly") {
				await subscribe(selectedPlan);
			} else if (selectedPlan === "free") {
				successToast("Uspješno ste završili prijavu!");
				navigate("/", { replace: true });
			}
		};

		handleSubscription();
	}, [user]);

	// TODO: add spinner
	if (isPending) return <></>;

	const plans = [
		{
			name: "Mjesečni plan",
			originalPrice: priceInfo?.compare_at_price_monthly,
			price: priceInfo?.price_monthly,
			savingsFrom: Math.max(
				priceInfo?.compare_at_price_monthly -
					priceInfo?.price_monthly || 0,
				0
			),
			benefits: [
				{
					name: "Pristup svom sadržaju",
					check: true,
				},
				{
					name: "AI asistencija do 5000 kredita",
					check: true,
				},
				{
					name: "Provjere znanja",
					check: true,
				},
			],
			isLoading: false,
			preferred: false,
			onSelect: async () => {
				if (user) {
					await subscribe("monthly");
				} else {
					localStorage.setItem("skipPayment", "true");
					localStorage.setItem("selectedPlan", "monthly");
					navigate("/register");
				}
			},
		},
		{
			name: "Godišnji plan",
			originalPrice: priceInfo?.compare_at_price_yearly,
			price: priceInfo?.price_yearly,
			savingsFrom: Math.max(
				priceInfo?.compare_at_price_yearly - priceInfo?.price_yearly ||
					0,
				0
			),
			benefits: [
				{
					name: "Pristup svom sadržaju",
					check: true,
				},
				{
					name: "AI asistencija do 5000 kredita",
					check: true,
				},
				{
					name: "Provjere znanja",
					check: true,
				},
			],
			generationCredits: "Unlimited",
			isLoading: isPending,
			onSelect: async () => {
				if (user) {
					await subscribe("yearly");
				} else {
					localStorage.setItem("skipPayment", "true");
					localStorage.setItem("selectedPlan", "yearly");
					navigate("/register");
				}
			},
			preferred: true,
		},
	];

	return (
		<div className={style.content}>
			<div className={style.header}>
				<Icon icon="fa-tasks" />
				<h2>Pretplatite se</h2>
				<div className={style.currencyNotice}>
					<Icon icon="fa-info-circle" />
					<span>
						Sva plaćanja se izvršavaju u KM (BAM). Iznos koji se
						tereti na vašoj kartici konvertuje se u vašu lokalnu
						valutu prema kursu kartičnih asocijacija.
					</span>
				</div>
			</div>
			<div className={style.body}>
				<div className={style.offersHolder}>
					{plans.map((plan) => (
						<div
							className={`${style.offer} ${
								plan.preferred ? style.preferred : ""
							}`}
							key={plan.name}
						>
							{plan.preferred && (
								<div className={style.badge}>
									NAJPOPULARNIJE
									<Icon icon="fa-crown" />
								</div>
							)}
							<div className={style.offerHeader}>
								<h3>{plan.name}</h3>
							</div>
							<div className={style.offerBody}>
								<ul>
									{plan.benefits.map((benefit, index) => (
										<li key={index}>
											<Icon
												icon={
													benefit.check
														? "fa-check"
														: "fa-times"
												}
											/>
											<span>{benefit.name}</span>
										</li>
									))}
								</ul>
							</div>
							<div className={style.offerFooter}>
								<div className={style.priceSection}>
									<div className={style.originalPrice}>
										<span>ORIGINALNA CIJENA:</span>
										<span className={style.strikethrough}>
											{plan.originalPrice.toLocaleString(
												"en-US"
											)}{" "}
											KM
										</span>
									</div>
									<div className={style.currentPrice}>
										<div className={style.priceAmount}>
											{plan.price.toLocaleString(
												"en-US",
												{
													minimumFractionDigits: 0,
													maximumFractionDigits: 2,
												}
											)}{" "}
											KM
										</div>
									</div>
									<div className={style.savings}>
										UŠTEDA OD:{" "}
										{plan.savingsFrom.toLocaleString(
											"en-US"
										)}{" "}
										KM
									</div>
								</div>
								<Button
									onClick={plan.onSelect}
									className={style.btn}
								>
									{plan.isLoading ? <Spinner /> : "Nastavi"}
								</Button>
							</div>
						</div>
					))}
				</div>
			</div>
			<div className={style.securitySection}>
				<div className={style.securityLogos}>
					<img
						src={visaSecure}
						alt="Visa Secure"
						className={style.securityLogo}
						title="Vaše transakcije su sigurne uz Visa plaćanje"
					/>
					<img
						src={mastercardIdCheck}
						alt="Mastercard® Identity Check™"
						className={style.securityLogo}
					/>
					<a
						href="http://monri.com/"
						target="_blank"
						rel="noopener noreferrer"
					>
						<img
							src={monriBadge}
							alt="Monri Payments - PSP"
							className={style.securityLogo}
						/>
					</a>
				</div>

				<div className={style.security}>
					<Icon icon="fa-shield-check" />
					<div className={style.column}>
						<span>Plaćanje uz maksimalnu sigurnost.</span>
						<span>Povrat novca zagarantovan.</span>
					</div>
				</div>
			</div>

			{/* Credit Card Purchase Security Statement - Monri Compliance Section 1.5.1 */}
			<div className={style.securityStatementSection}>
				<div className={style.securityStatementHeader}>
					<Icon icon="fa-shield-check" />
					<h3>Izjava o sigurnosti kupovine kreditnom karticom</h3>
				</div>
				<div className={style.securityStatementContent}>
					<p>
						Povjerljivost vaših podataka zaštićena je i osigurana
						korištenjem najnovije verzije TLS enkripcije. Stranice
						za online naplatu su osigurane korištenjem Secure Socket
						Layer (SSL) protokola sa 128-bitnom enkripcijom
						podataka. SSL enkripcija je proces šifrovanja podataka
						koji sprečava neovlašten pristup tokom njihovog
						prijenosa.
					</p>
					<p>
						Ovo omogućava siguran prijenos podataka i sprečava
						neovlašten pristup podacima tokom komunikacije između
						korisnika i Monri WebPay Payment Gateway-a i obrnuto.
					</p>
					<p>
						Monri WebPay Payment Gateway i finansijske institucije
						razmjenjuju podatke koristeći svoju virtualnu privatnu
						mrežu (VPN) koja je takođe zaštićena od neovlašćenog
						pristupa.
					</p>
					<p>
						Monri Payments je PCI DSS Level 1 certificirani pružalac
						usluga plaćanja regulisan pravilima Visa i Mastercard.
					</p>
					<p>
						Brojevi kreditnih kartica nisu pohranjeni kod trgovca i
						nisu dostupni neovlašćenom osoblju.
					</p>
				</div>
			</div>

			{/* Terms and Conditions Link - Monri Compliance */}
			<div className={style.termsSection}>
				<Icon icon="fa-file-contract" />
				<span>
					Nastavljajući plaćanje, slažete se sa našim{" "}
					<a href="/terms-and-conditions" target="_blank">
						Uslovima korištenja
					</a>{" "}
					i{" "}
					<a href="/privacy-policy" target="_blank">
						Politikom privatnosti
					</a>
					.
				</span>
			</div>
		</div>
	);
}

export default ChoosePlan;
